import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Award, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { Footer } from "../components/Footer";
import api, { formatApiError } from "../lib/api";
import CoachCelebration from "../components/CoachCelebration";
import { pick } from "../lib/pick";

export default function AcademyQuiz() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [coach, setCoach] = useState(false);

  const load = () => {
    setResult(null);
    setAnswers({});
    api.get(`/academy/courses/${slug}/quiz`, { params: { lang } }).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  };

  useEffect(load, [slug, lang]);

  const submit = async () => {
    try {
      const { data: res } = await api.post(`/academy/courses/${slug}/quiz`, { answers }, { params: { lang } });
      setResult(res);
      if (res.passed) { toast.success(t("academy.passed")); setCoach(true); }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-24 pb-24 text-center" data-testid="academy-quiz-error">
        <p className="text-[#0B1B3A] font-semibold">{error}</p>
        <Link to="/academy" className="btn-blue mt-6 inline-flex">{t("academy.backToCatalog")}</Link>
      </div>
    );
  }

  if (!data) return <p className="max-w-3xl mx-auto px-5 pt-20 text-[#64748B]">{t("common.loading")}</p>;

  const allAnswered = Object.keys(answers).length === data.questions.length;

  return (
    <>
      <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-12 pb-20" data-testid="academy-quiz-page">
        <CoachCelebration
          open={coach}
          onClose={() => setCoach(false)}
          title={t("coach.courseDone")}
          subtitle={result ? `${data.course.title} · ${result.score}%` : ""}
          badges={result?.badges || []}
          newBadges={result?.new_badges || []}
          courseBadgeUrl={result?.passed ? `${process.env.REACT_APP_BACKEND_URL}/api/badges/course/${data.course.id}.png` : ""}
        />
        <Link data-testid="academy-quiz-back" to={`/academy/${slug}`} className="inline-flex items-center gap-2 text-sm font-bold text-[#334155] hover:text-[#1D4ED8] transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" /> {pick(data.course, "title", lang)}
        </Link>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mt-5 text-[#0B1B3A]" data-testid="academy-quiz-title">{t("academy.finalQuiz")}</h1>
        <p className="text-sm text-[#475569] mt-3">
          {t("academy.quizIntro")} · {t("academy.attempts")}: {data.attempts}
        </p>

        {result ? (
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="card-dark p-8 sm:p-10 mt-8" data-testid="academy-quiz-result">
            <p className={`font-heading font-black text-5xl ${result.passed ? "text-[#16A34A]" : "text-[#B91C1C]"}`} data-testid="academy-quiz-score">
              {result.score}%
            </p>
            <p className="text-sm font-bold text-[#0B1B3A] mt-4">
              {result.correct}/{result.total} · {result.passed ? t("academy.passed") : t("academy.failed")}
            </p>
            {result.passed ? (
              <div className="mt-7 flex flex-wrap gap-3">
                {result.path_diplomas?.map((d) => (
                  <Link key={d.id} data-testid={`academy-path-diploma-${d.id}`} to="/certificates" className="btn-dark">
                    <Award className="w-4 h-4" /> {t("certs.diploma")}: {pick(d, "course_title", lang)}
                  </Link>
                ))}
                {result.certificate?.verification_url ? (
                  <a data-testid="academy-verify-certificate-link" href={result.certificate.verification_url} target="_blank" rel="noreferrer" className="btn-blue">
                    <Award className="w-4 h-4" /> {t("academy.verifyCertificate")}
                  </a>
                ) : (
                  <Link data-testid="academy-certificate-link" to={`/academy/${slug}`} className="btn-blue">
                    <Award className="w-4 h-4" /> {t("academy.certificateIssued")}
                  </Link>
                )}
              </div>
            ) : (
              <button data-testid="academy-quiz-retry" onClick={load} className="btn-blue mt-7">
                <RotateCcw className="w-4 h-4" /> {t("academy.retry")}
              </button>
            )}
            <div className="mt-8 divide-y divide-[#E2E8F0]" data-testid="academy-quiz-review">
              {result.review.map((r, i) => {
                const ok = r.given === r.correct_index;
                return (
                  <div key={i} className="py-3 flex items-start gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${ok ? "bg-[#1D4ED8] text-white" : "bg-[#FEE2E2] text-[#B91C1C]"}`}>
                      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                    </span>
                    <p className="text-sm text-[#475569]">{r.question}</p>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ) : (
          <>
            <div className="mt-8 space-y-4" data-testid="academy-quiz-questions">
              {data.questions.map((q, qi) => (
                <div key={q.id} className="card-dark p-6" data-testid={`academy-quiz-question-${qi}`}>
                  <p className="font-bold text-[#0B1B3A]">{qi + 1}. {q.question}</p>
                  <div className="grid grid-cols-1 gap-2 mt-4">
                    {q.options.map((opt, oi) => (
                      <button
                        key={oi}
                        data-testid={`academy-quiz-${qi}-option-${oi}`}
                        onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                        className={`text-left rounded-[16px] border px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
                          answers[q.id] === oi ? "border-[#1D4ED8] bg-[#EEF4FF] text-[#0B1B3A]" : "border-[#E2E8F0] text-[#1E293B] hover:border-[#1D4ED8]"
                        }`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="academy-quiz-submit" disabled={!allAnswered} onClick={submit} className="btn-blue mt-8 disabled:opacity-50">
              {t("academy.submitQuiz")}
            </button>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
